import React, { useEffect, useMemo, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
    Plus, Edit2, Trash2, Save, X, Download, FileDown,
    Upload, FileSpreadsheet, BookOpen, Layers, Link2,
    Unlink, Eye, CheckCircle, XCircle, BarChart2, FileText,
} from "lucide-react";

import {
    getAllJobsRequest,
    createJobRequest,
    updateJobRequest,
    deleteJobRequest,
    importJobsExcelRequest,
    exportJobsRequest,
    downloadJobsTemplateRequest,
    clearJobsImportResult,
} from "../../redux/actions/Jobactions";

import {
    fetchJobSkillsRequest,
    createJobSkillRequest,
    updateJobSkillRequest,
    deleteJobSkillRequest,
    importJobSkillsExcelRequest,
    downloadJobSkillsTemplateRequest,
    clearJobSkillsImportResult,
} from "../../redux/actions/jobSkillActions";

import { fetchSkillsRequest }      from "../../redux/actions/skillsActions";
import { fetchNiveauSkillsRequest } from "../../redux/actions/niveauSkillActions";
import { fetchModesEvaluationRequest } from "../../redux/actions/modeEvaluationActions";
import ScrollToTop from "../../Ui/UiJs/ScrollToTop";

/* ════════════════════════════════════════
   MODAL réutilisable
════════════════════════════════════════ */
const Modal = ({ open, title, onClose, size = "lg", footer, children, panelStyle }) => {
    if (!open) return null;
    const sizeClass =
        size === "xl" ? "crModal__panel--xl" :
            size === "lg" ? "crModal__panel--lg" : "";
    return (
        <div className="crModal" role="dialog" aria-modal="true" onMouseDown={onClose}>
            <div className={`crModal__panel ${sizeClass}`} style={panelStyle} onMouseDown={(e) => e.stopPropagation()}>
                <div className="crModal__header">
                    <div className="crModal__title">{title}</div>
                    <button className="crIconBtn" type="button" onClick={onClose} aria-label="Fermer">
                        <X size={18} />
                    </button>
                </div>
                <div className="crModal__body">{children}</div>
                {footer && <div className="crModal__footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ════════════════════════════════════════
   MODAL DÉTAILS DU POSTE
════════════════════════════════════════ */
const JobDetailsModal = ({ open, job, onClose, dispatch }) => {
    const { jobSkills, loading: jsLoading } = useSelector((s) => s.jobSkill || {});
    const { list: skillsList } = useSelector((s) => s.skills || {});
    const [domaineFilter, setDomaineFilter] = useState("");
    const [themeFilter, setThemeFilter] = useState("");

    useEffect(() => {
        if (open && job?.id) {
            dispatch(fetchJobSkillsRequest(job.id));
            dispatch(fetchSkillsRequest());
        }
    }, [open, job?.id, dispatch]);

    useEffect(() => {
        if (open) {
            setDomaineFilter("");
            setThemeFilter("");
        }
    }, [open, job?.id]);

    const assocs = useMemo(() => Array.isArray(jobSkills) ? jobSkills : [], [jobSkills]);
    const skills = useMemo(() => Array.isArray(skillsList) ? skillsList : [], [skillsList]);
    const skillsById = useMemo(() => {
        const map = new Map();
        skills.forEach((s) => map.set(s.id, s));
        return map;
    }, [skills]);

    const enrichedAssocs = useMemo(() => assocs.map((a) => {
        const skill = skillsById.get(a.skillId) || {};
        return {
            ...a,
            domaineNom: a.domaineNom || a.domaineName || a.skillDomaineNom || skill.domaineNom || skill.domaineName || skill.skillDomaineNom || "",
            themeNom: a.themeNom || a.themeName || a.skillThemeNom || skill.themeNom || skill.themeName || skill.skillThemeNom || "",
        };
    }), [assocs, skillsById]);

    const filterOptions = useMemo(() => {
        const unique = (values) => [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
        const themesSource = domaineFilter
            ? enrichedAssocs.filter((a) => a.domaineNom === domaineFilter)
            : enrichedAssocs;
        return {
            domaines: unique(enrichedAssocs.map((a) => a.domaineNom)),
            themes: unique(themesSource.map((a) => a.themeNom)),
        };
    }, [enrichedAssocs, domaineFilter]);

    const filteredAssocs = useMemo(() => enrichedAssocs.filter((a) =>
        (!domaineFilter || a.domaineNom === domaineFilter) &&
        (!themeFilter || a.themeNom === themeFilter)
    ), [enrichedAssocs, domaineFilter, themeFilter]);

    const levelBadgeClass = (n) => {
        if (n <= 1) return "crBadge--grey";
        if (n <= 2) return "crBadge--green";
        if (n <= 3) return "crBadge--orange";
        return "crBadge--red";
    };

    if (!open || !job) return null;

    return (
        <Modal
            open={open}
            title={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Eye size={16} style={{ color: "#ee645f" }} />
                    Détails du poste
                </span>
            }
            onClose={onClose}
            size="xl"
            panelStyle={{
                width: "min(1120px, calc(100vw - 80px))",
                maxWidth: "calc(100vw - 80px)",
            }}
            footer={
                <button className="crBtn crBtn--ghost" onClick={onClose} type="button">Fermer</button>
            }
        >
            <div className="crFormContent">

                {/* ── Infos du poste ── */}
                <div className="crFormSection">
                    <h3 className="crFormTitle">Informations du poste</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 16px" }}>
                        <div className="jd-info-block">
                            <span className="jd-info-label">Code : </span>
                            <span className="crStrong">{job.code}</span>
                        </div>
                        <div className="jd-info-block">
                            <span className="jd-info-label">Nom : </span>
                            <span className="jd-info-value">{job.name}</span>
                        </div>
                        <div className="jd-info-block">
                            <span className="jd-info-label">Statut : </span>
                            {job.isActive
                                ? <span className="crBadge crBadge--green"><CheckCircle size={11} style={{ marginRight: 4 }} />Actif</span>
                                : <span className="crBadge crBadge--grey"><XCircle size={11} style={{ marginRight: 4 }} />Inactif</span>
                            }
                        </div>
                        {job.description && (
                            <div className="jd-info-block" style={{ gridColumn: "1 / -1" }}>
                                <span className="jd-info-label">Description : </span>
                                <span className="jd-info-value">{job.description}</span>
                            </div>
                        )}
                        {/*<div className="jd-info-block">*/}
                        {/*    <span className="jd-info-label">Créé le</span>*/}
                        {/*    <span className="jd-info-value crMuted">*/}
                        {/*        {job.createdAt ? new Date(job.createdAt).toLocaleDateString("fr-FR") : "—"}*/}
                        {/*    </span>*/}
                        {/*</div>*/}
                        {/*<div className="jd-info-block">*/}
                        {/*    <span className="jd-info-label">Modifié le</span>*/}
                        {/*    <span className="jd-info-value crMuted">*/}
                        {/*        {job.updatedAt ? new Date(job.updatedAt).toLocaleDateString("fr-FR") : "—"}*/}
                        {/*    </span>*/}
                        {/*</div>*/}
                    </div>
                </div>

                {/* ── Statistiques ── */}
                {/*<div className="crFormSection">*/}
                {/*    <h3 className="crFormTitle">*/}
                {/*        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>*/}
                {/*            <BarChart2 size={13} /> Statistiques*/}
                {/*        </span>*/}
                {/*    </h3>*/}
                {/*    <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>*/}
                {/*        <div className="jd-stat-card">*/}
                {/*            <span className="jd-stat-value">{filteredAssocs.length}</span>*/}
                {/*            <span className="jd-stat-label"> compétence{filteredAssocs.length !== 1 ? "s" : ""} requise{filteredAssocs.length !== 1 ? "s" : ""}</span>*/}
                {/*        </div>*/}
                {/*        <div className="jd-stat-card">*/}
                {/*            <span className="jd-stat-label">Niveau max requis : </span>*/}
                {/*            <span className="jd-stat-value">*/}
                {/*                {filteredAssocs.length > 0 ? Math.max(...filteredAssocs.map((a) => a.niveau || 0)) : "—"}*/}
                {/*            </span>*/}
                {/*        </div>*/}
                {/*        <div className="jd-stat-card">*/}
                {/*            <span className="jd-stat-label">Niveau moyen requis : </span>*/}
                {/*            <span className="jd-stat-value">*/}
                {/*                {filteredAssocs.length > 0*/}
                {/*                    ? (filteredAssocs.reduce((s, a) => s + (a.niveau || 0), 0) / filteredAssocs.length).toFixed(1)*/}
                {/*                    : "—"}*/}
                {/*            </span>*/}
                {/*        </div>*/}
                {/*    </div>*/}
                {/*</div>*/}

                {/* ── Compétences associées ── */}
                <div className="crFormSection">
                    <h3 className="crFormTitle"
                        style={{display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Compétences requises</span>
                        <span style={{ fontSize: 11, color: "#6b7280", textTransform: "none", letterSpacing: 0, fontWeight: 600 }}>
                            {filteredAssocs.length} / {enrichedAssocs.length} competences
                        </span>
                    </h3>
                    <div className="crFormGrid" style={{ gridTemplateColumns: "repeat(2, minmax(220px, 1fr))", marginBottom: 14 }}>
                        <div className="crField">
                            <label>Domaine</label>
                            <select
                                value={domaineFilter}
                                onChange={(e) => {
                                    setDomaineFilter(e.target.value);
                                    setThemeFilter("");
                                }}
                            >
                                <option value="">Tous les domaines</option>
                                {filterOptions.domaines.map((domaine) => (
                                    <option key={domaine} value={domaine}>{domaine}</option>
                                ))}
                            </select>
                        </div>
                        <div className="crField">
                            <label>Theme</label>
                            <select value={themeFilter} onChange={(e) => setThemeFilter(e.target.value)}>
                                <option value="">Tous les themes</option>
                                {filterOptions.themes.map((theme) => (
                                    <option key={theme} value={theme}>{theme}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="crTableWrap">
                        <table className="crTable">
                            <thead>
                            <tr>
                                <th>Compétence</th>
                                <th>Code</th>
                                <th>Niveau requis</th>
                            </tr>
                            </thead>
                            <tbody>
                            {jsLoading ? (
                                <tr><td colSpan={3}><div className="crLoading"><div className="crSpinner" /><span>Chargement...</span></div></td></tr>
                            ) : enrichedAssocs.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="crEmptyCell">
                                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "8px 0" }}>
                                            <Unlink size={22} style={{ color: "#d1d5db" }} />
                                            <span>Aucune compétence associée à ce poste</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredAssocs.length === 0 ? (
                                <tr>
                                    <td colSpan={3} className="crEmptyCell">
                                        Aucun resultat pour ces filtres
                                    </td>
                                </tr>
                            ) : filteredAssocs.map((a) => (
                                <tr key={a.id}>
                                    <td style={{ fontWeight: 600, color: "#204385", fontSize: 12 }}>{a.skillNom}</td>
                                    <td><span className="crStrong">{a.skillCode}</span></td>
                                    <td>
                                        <span className={`crBadge ${levelBadgeClass(a.niveau)}`}>
                                            <Layers size={11} style={{ marginRight: 4 }} />
                                            Niveau {a.niveau} — {a.niveauSkillNom}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </Modal>
    );
};

/* ════════════════════════════════════════
   MODAL ASSOCIATION compétences/niveaux
════════════════════════════════════════ */
const JobSkillsModal = ({ open, job, onClose, dispatch }) => {
    const { jobSkills, loading: jsLoading } = useSelector((s) => s.jobSkill   || {});
    const { list: skillsList }              = useSelector((s) => s.skills      || {});
    const { niveauSkills }                  = useSelector((s) => s.niveauSkill || {});

    const [selectedSkillIds, setSelectedSkillIds] = useState([]);
    const [niveauId,  setNiveauId]  = useState("");
    const [editingId, setEditingId] = useState(null);
    const [skillSearch, setSkillSearch] = useState("");
    const [showSkillDropdown, setShowSkillDropdown] = useState(false);
    const [previewList, setPreviewList] = useState([]);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowSkillDropdown(false);
            }
        };
        if (showSkillDropdown) {
            window.addEventListener("mousedown", handleClickOutside, true);
        }
        return () => window.removeEventListener("mousedown", handleClickOutside, true);
    }, [showSkillDropdown]);

    const { modesEvaluation } = useSelector((s) => s.modesEvaluation || {});

    const [modeId, setModeId] = useState("");

    useEffect(() => {
        if (open && job?.id) {
            dispatch(fetchJobSkillsRequest(job.id));
            dispatch(fetchSkillsRequest());
            dispatch(fetchNiveauSkillsRequest());
            dispatch(fetchModesEvaluationRequest());
        }
    }, [open, job?.id, dispatch]);

    const skills  = useMemo(() => Array.isArray(skillsList)  ? skillsList  : [], [skillsList]);
    const niveaux = useMemo(() => Array.isArray(niveauSkills) ? [...niveauSkills].sort((a, b) => a.niveau - b.niveau) : [], [niveauSkills]);
    const assocs  = useMemo(() => Array.isArray(jobSkills)   ? jobSkills   : [], [jobSkills]);

    const usedSkillIds = useMemo(
        () => new Set(assocs.filter((a) => a.id !== editingId).map((a) => a.skillId)),
        [assocs, editingId]
    );

    const resetForm = () => { setSelectedSkillIds([]); setNiveauId(""); setModeId(""); setEditingId(null); setSkillSearch(""); setShowSkillDropdown(false); };
    const clearStaging = () => { setPreviewList([]); resetForm(); };
    const handleClose = () => { clearStaging(); onClose(); };

    const openEditAssoc = (a) => {
        setEditingId(a.id);
        setSelectedSkillIds([a.skillId]);
        setNiveauId(String(a.niveauSkillId));
        setModeId(a.modeEvaluationId ? String(a.modeEvaluationId) : "");
    };

    const addToStaging = () => {
        if (selectedSkillIds.length === 0 || !niveauId) {
            Swal.fire({ icon: "warning", text: "Veuillez choisir au moins une compétence et un niveau." });
            return;
        }

        const newItems = selectedSkillIds.map(sid => {
            const skill = skills.find(s => s.id === sid);
            const niveau = niveauSkills.find(n => n.id === Number(niveauId));
            const mode = modesEvaluation.find(m => m.id === Number(modeId));
            
            return {
                id: Date.now() + Math.random(), // temp id for list
                skillId: sid,
                skillNom: skill?.nom,
                skillCode: skill?.code,
                niveauId: Number(niveauId),
                niveauNom: niveau?.nom,
                modeId: modeId ? Number(modeId) : null,
                modeNom: mode?.nom || "Non défini"
            };
        });

        // Check for duplicates in staging
        const duplicates = newItems.filter(ni => previewList.some(pi => pi.skillId === ni.skillId));
        if (duplicates.length > 0) {
            Swal.fire({ icon: "info", text: "Certaines compétences sont déjà dans votre liste de préparation." });
        }

        const uniqueNewItems = newItems.filter(ni => !previewList.some(pi => pi.skillId === ni.skillId));
        setPreviewList([...previewList, ...uniqueNewItems]);
        resetForm();
    };

    const removeFromStaging = (tempId) => {
        setPreviewList(previewList.filter(item => item.id !== tempId));
    };

    const saveAllStaging = () => {
        if (previewList.length === 0) return;
        
        previewList.forEach(item => {
            const payload = {
                jobId: job.id,
                skillId: item.skillId,
                niveauSkillId: item.niveauId,
                modeEvaluationId: item.modeId,
            };
            dispatch(createJobSkillRequest(payload));
        });

        Swal.fire({ icon: "success", title: "Enregistré", text: `${previewList.length} association(s) créée(s) avec succès.` });
        clearStaging();
    };

    const submitEditAssoc = () => {
        if (selectedSkillIds.length === 0 || !niveauId) return;
        const payload = {
            jobId:             job.id,
            skillId:           selectedSkillIds[0],
            niveauSkillId:     Number(niveauId),
            modeEvaluationId:  modeId ? Number(modeId) : null,
        };
        dispatch(updateJobSkillRequest(editingId, payload));
        resetForm();
    };

    const toggleSkill = (id) => {
        if (editingId) return;
        setSelectedSkillIds(prev => 
            prev.includes(id) ? prev.filter(sid => sid !== id) : [...prev, id]
        );
    };

    const confirmRemove = (a) => {
        Swal.fire({
            title: "Retirer",
            text: `Retirer "${a.skillNom}" du poste ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
            confirmButtonColor: "#ee645f",
        }).then((r) => { if (r.isConfirmed) dispatch(deleteJobSkillRequest(a.id)); });
    };

    const levelBadgeClass = (n) => {
        if (n <= 1) return "crBadge--grey";
        if (n <= 2) return "crBadge--green";
        if (n <= 3) return "crBadge--orange";
        return "crBadge--red";
    };

    if (!open) return null;

    return (
        <Modal
            open={open}
            title={
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <BookOpen size={16} style={{ color: "#ee645f" }} />
                    Association des compétences —&nbsp;
                    <span style={{ color: "#ee645f" }}>{job?.name}</span>
                </span>
            }
            onClose={handleClose}
            size="xl"
            panelStyle={{
                width: "min(1180px, calc(100vw - 80px))",
                maxWidth: "calc(100vw - 80px)",
            }}
            footer={
                <button className="crBtn crBtn--ghost" onClick={handleClose} type="button">Fermer</button>
            }
        >
            <div className="crFormContent">

                {/* ── Formulaire d'ajout / édition ── */}
                <div className="crFormSection">
                    <h3 className="crFormTitle">
                        {editingId ? "Modifier l'association" : "Associer une compétence"}
                    </h3>
                    <div className="crFormGrid" style={{gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr)) auto"}}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px", gridColumn: "1 / -1", position: "relative" }} ref={dropdownRef}>
                            <label style={{ fontSize: "11px", fontWeight: "800", color: "#204385", textTransform: "uppercase", letterSpacing: ".025em" }}>Compétences *</label>
                            <div style={{ position: "relative" }}>
                                <div 
                                    style={{ 
                                        display: "flex", 
                                        flexWrap: "wrap", 
                                        gap: "6px", 
                                        minHeight: "42px", 
                                        width: "100%",
                                        cursor: "text",
                                        padding: "6px 12px",
                                        alignItems: "center",
                                        border: "1px solid #d1d5db",
                                        borderRadius: "10px",
                                        background: "#fff"
                                    }}
                                    onClick={() => !editingId && setShowSkillDropdown(true)}
                                >
                                    {selectedSkillIds.map(id => {
                                        const s = skills.find(sk => sk.id === id);
                                        return (
                                            <span key={id} className="crBadge crBadge--blue" style={{ gap: "6px", padding: "4px 8px" }}>
                                                {s?.nom}
                                                {!editingId && <X size={12} style={{ cursor: "pointer" }} onClick={(e) => { e.stopPropagation(); toggleSkill(id); }} />}
                                            </span>
                                        );
                                    })}
                                    <input 
                                        type="text" 
                                        placeholder={selectedSkillIds.length === 0 ? "Rechercher une compétence..." : ""}
                                        value={skillSearch}
                                        onChange={(e) => { setSkillSearch(e.target.value); setShowSkillDropdown(true); }}
                                        onFocus={() => setShowSkillDropdown(true)}
                                        disabled={!!editingId}
                                        style={{ 
                                            border: "none", 
                                            outline: "none", 
                                            boxShadow: "none", 
                                            background: "transparent", 
                                            flex: 1, 
                                            minWidth: "120px", 
                                            padding: 0, 
                                            height: "100%",
                                            color: "#204385",
                                            fontSize: "12px",
                                            fontWeight: "500"
                                        }}
                                    />
                                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                                        {(skillSearch || showSkillDropdown) && (
                                            <button 
                                                type="button" 
                                                style={{ border: "none", background: "transparent", cursor: "pointer", color: "#6b7280", padding: "4px" }}
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    setSkillSearch(""); 
                                                    setShowSkillDropdown(false); 
                                                }}
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {showSkillDropdown && !editingId && (
                                    <div style={{ 
                                        position: "absolute", 
                                        top: "100%", 
                                        left: 0, 
                                        right: 0, 
                                        zIndex: 10000, 
                                        background: "#fff", 
                                        border: "1px solid #d1d5db", 
                                        borderRadius: "10px", 
                                        marginTop: "4px", 
                                        maxHeight: "220px", 
                                        overflowY: "auto",
                                        boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)"
                                    }}>
                                        {skills
                                            .filter(s => 
                                                !skillSearch || 
                                                s.nom.toLowerCase().includes(skillSearch.toLowerCase()) || 
                                                s.code.toLowerCase().includes(skillSearch.toLowerCase())
                                            )
                                            .map((s) => {
                                                const isUsed = usedSkillIds.has(s.id) && !selectedSkillIds.includes(s.id);
                                                const isSelected = selectedSkillIds.includes(s.id);
                                                return (
                                                    <div 
                                                        key={s.id}
                                                        onClick={() => { if (!isUsed) toggleSkill(s.id); }}
                                                        style={{ 
                                                            padding: "10px 14px", 
                                                            cursor: isUsed ? "not-allowed" : "pointer",
                                                            background: isSelected ? "#fde8e7" : "transparent",
                                                            opacity: isUsed ? 0.5 : 1,
                                                            fontSize: "12px",
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            borderBottom: "1px solid #f3f4f6",
                                                            transition: "background 0.2s"
                                                        }}
                                                        onMouseEnter={(e) => !isUsed && !isSelected && (e.currentTarget.style.background = "#f9fafb")}
                                                        onMouseLeave={(e) => !isUsed && !isSelected && (e.currentTarget.style.background = "transparent")}
                                                    >
                                                        <span>
                                                            <span style={{ fontWeight: 600, color: "#204385" }}>{s.nom}</span>
                                                            <span className="crMuted" style={{ fontSize: 10, marginLeft: 6 }}>({s.code})</span>
                                                        </span>
                                                        {isSelected && <CheckCircle size={14} style={{ color: "#ee645f" }} />}
                                                        {isUsed && <span className="crBadge crBadge--grey" style={{ fontSize: 9 }}>Affectée</span>}
                                                    </div>
                                                );
                                            })
                                        }
                                        {skills.filter(s => !skillSearch || s.nom.toLowerCase().includes(skillSearch.toLowerCase()) || s.code.toLowerCase().includes(skillSearch.toLowerCase())).length === 0 && (
                                            <div style={{ padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "12px" }}>
                                                Aucune compétence trouvée
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="crField">
                            <label>Niveau requis *</label>
                            <select value={niveauId} onChange={(e) => setNiveauId(e.target.value)}>
                                <option value="">-- choisir --</option>
                                {niveaux.map((n) => (
                                    <option key={n.id} value={n.id}>Niveau {n.niveau} — {n.nom}</option>
                                ))}
                            </select>
                        </div>
                        <div className="crField">
                            <label>Mode d'évaluation</label>
                            <select value={modeId} onChange={(e) => setModeId(e.target.value)}>
                                <option value="">-- aucun --</option>
                                {(Array.isArray(modesEvaluation) ? modesEvaluation : []).map((m) => (
                                    <option key={m.id} value={m.id}>{m.nom} ({m.code})</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
                            {editingId && (
                                <button className="crBtn crBtn--ghost" type="button" onClick={resetForm}>
                                    <X size={14}/> Annuler
                                </button>
                            )}
                            <button 
                                className="crBtn crBtn--primary" 
                                type="button" 
                                onClick={editingId ? submitEditAssoc : addToStaging}
                                disabled={jsLoading}
                                style={{ minWidth: "160px", justifyContent: "center" }}
                            >
                                {editingId ? <><Save size={14}/> Enregistrer</> : <><Plus size={14}/> Ajouter à la liste</>}
                            </button>
                        </div>
                    </div>

                    {/* ── Prévisualisation avant enregistrement ── */}
                    {!editingId && previewList.length > 0 && (
                        <div style={{ marginTop: "24px", borderTop: "2px dashed #e5e7eb", paddingTop: "18px", marginBottom: "24px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                                <h4 style={{ margin: 0, fontSize: "13px", color: "#204385", fontWeight: "800" }}>
                                    Liste de préparation ({previewList.length})
                                </h4>
                                <button className="jm-link-btn danger" type="button" onClick={() => setPreviewList([])}>Tout vider</button>
                            </div>
                            <div className="crTableWrap" style={{ maxHeight: "250px" }}>
                                <table className="crTable">
                                    <thead>
                                        <tr>
                                            <th>Compétence</th>
                                            <th>Niveau</th>
                                            <th>Mode</th>
                                            <th className="is-right">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewList.map((item) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <div style={{ fontWeight: 700, color: "#204385" }}>{item.skillNom}</div>
                                                    <div className="crMuted" style={{ fontSize: 10 }}>{item.skillCode}</div>
                                                </td>
                                                <td><span className="crBadge crBadge--blue">{item.niveauNom}</span></td>
                                                <td><span className="crBadge crBadge--grey">{item.modeNom}</span></td>
                                                <td className="is-right">
                                                    <button className="crIconBtn crIconBtn--danger" type="button" onClick={() => removeFromStaging(item.id)}>
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ marginTop: "18px", display: "flex", justifyContent: "flex-end" }}>
                                <button 
                                    className="crBtn crBtn--primary" 
                                    type="button" 
                                    onClick={saveAllStaging} 
                                    style={{ background: "#059669", boxShadow: "0 4px 12px rgba(5, 150, 105, 0.2)" }}
                                >
                                    <Save size={16} /> Enregistrer tout ({previewList.length})
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Liste des associations ── */}
                <div className="crFormSection">
                    <h3 className="crFormTitle"
                        style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                        <span>Compétences associées</span>
                        <span style={{
                            fontSize: 11,
                            color: "#6b7280",
                            textTransform: "none",
                            letterSpacing: 0,
                            fontWeight: 600
                        }}>
                            {assocs.length} compétence{assocs.length !== 1 ? "s" : ""}
                        </span>
                    </h3>
                    <div className="crTableWrap">
                        <table className="crTable">
                            <thead>
                            <tr>
                                <th>Compétence</th>
                                <th>Code</th>
                                <th>Niveau requis</th>
                                <th>Mode d'évaluation</th>
                                <th className="is-right">Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {jsLoading ? (
                                <tr>
                                    <td colSpan={5}>
                                        <div className="crLoading">
                                            <div className="crSpinner"/>
                                            <span>Chargement...</span></div>
                                    </td>
                                </tr>
                            ) : assocs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="crEmptyCell">
                                        <div style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "8px 0"
                                        }}>
                                            <Unlink size={22} style={{color: "#d1d5db"}}/>
                                            <span>Aucune compétence associée à ce poste</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : assocs.map((a) => (
                                <tr key={a.id} style={{background: editingId === a.id ? "#fffbf0" : undefined}}>
                                    <td style={{fontWeight: 600, color: "#204385", fontSize: 12}}>{a.skillNom}</td>
                                    <td><span className="crStrong">{a.skillCode}</span></td>
                                    <td>
                                        <span className={`crBadge ${levelBadgeClass(a.niveau)}`}>
                                            <Layers size={11} style={{marginRight: 4}}/>
                                            Niveau {a.niveau} — {a.niveauSkillNom}
                                        </span>
                                    </td>
                                    <td>
                                        {a.modeEvaluationNom
                                            ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                    <span className="crBadge crBadge--blue">{a.modeEvaluationNom}</span>
                                                </div>
                                            )
                                            : <span className="crMuted">—</span>
                                        }
                                    </td>
                                    <td className="is-right">
                                        <div className="crRowActions">
                                            <button className="crIconBtn" title="Modifier le niveau"
                                                    onClick={() => openEditAssoc(a)} type="button">
                                                <Edit2 size={15}/>
                                            </button>
                                            <button className="crIconBtn crIconBtn--danger" title="Retirer"
                                                    onClick={() => confirmRemove(a)} type="button">
                                                <Unlink size={15}/>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

/* ════════════════════════════════════════
   ONGLET — LISTE DES POSTES
════════════════════════════════════════ */
const JobsTab = ({ jobs, loading, bulkLoading, importResult, dispatch, onOpenSkills, onOpenDetails }) => {

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing,     setEditing]     = useState(null);
    const [form,        setForm]        = useState({ code: "", name: "", description: "", isActive: true });
    const [q,           setQ]           = useState("");
    const [filter,      setFilter]      = useState("all");

    const [bulkOpen,   setBulkOpen]   = useState(false);
    const [bulkFile,   setBulkFile]   = useState(null);
    const [bulkDryRun, setBulkDryRun] = useState(true);
    const [bulkAction, setBulkAction] = useState("add");

    const [bulkJsOpen, setBulkJsOpen] = useState(false);
    const [bulkJsFile, setBulkJsFile] = useState(null);
    const [bulkJsDryRun, setBulkJsDryRun] = useState(true);
    const [bulkJsAction, setBulkJsAction] = useState("add");
    const { importResult: jsImportResult, loading: jsBulkLoading } = useSelector((s) => s.jobSkill || {});

    useEffect(() => { dispatch(getAllJobsRequest()); }, [dispatch]);

    const filtered = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        return (jobs || []).filter((j) => {
            const mQ = !t || (j.code || "").toLowerCase().includes(t) || (j.name || "").toLowerCase().includes(t) || (j.description || "").toLowerCase().includes(t);
            const mF = filter === "all" ? true : filter === "active" ? !!j.isActive : !j.isActive;
            return mQ && mF;
        });
    }, [jobs, q, filter]);

    const openCreate = () => { setEditing(null); setForm({ code: "", name: "", description: "", isActive: true }); setIsModalOpen(true); };
    const openEdit   = (j) => { setEditing(j); setForm({ code: j.code || "", name: j.name || "", description: j.description || "", isActive: j.isActive !== undefined ? j.isActive : true }); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditing(null); };
    const onChange   = (e) => { const { name, value, type, checked } = e.target; setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value })); };

    const submit = (e) => {
        e.preventDefault();
        if (!form.code?.trim()) { Swal.fire({ icon: "warning", text: "Code obligatoire." }); return; }
        if (!form.name?.trim()) { Swal.fire({ icon: "warning", text: "Nom obligatoire." });  return; }
        const payload = { code: form.code.trim(), name: form.name.trim(), description: form.description?.trim() || "", isActive: !!form.isActive };
        if (editing?.id) dispatch(updateJobRequest(editing.id, payload));
        else             dispatch(createJobRequest(payload));
        closeModal();
    };

    const confirmDelete = (j) => {
        Swal.fire({ title: "Supprimer", text: `Supprimer le poste "${j.name}" ?`, icon: "warning", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Non", confirmButtonColor: "#ee645f" })
            .then((r) => { if (r.isConfirmed) dispatch(deleteJobRequest(j.id)); });
    };

    const openBulk  = () => { setBulkOpen(true); setBulkFile(null); setBulkDryRun(true); setBulkAction("add"); dispatch(clearJobsImportResult()); };
    const closeBulk = () => { setBulkOpen(false); setBulkFile(null); dispatch(clearJobsImportResult()); };
    const runBulk   = () => {
        if (!bulkFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel." }); return; }
        dispatch(importJobsExcelRequest(bulkFile, bulkDryRun, bulkAction));
    };

    const openJsBulk  = () => { setBulkJsOpen(true); setBulkJsFile(null); setBulkJsDryRun(true); setBulkJsAction("add"); dispatch(clearJobSkillsImportResult()); };
    const closeJsBulk = () => { setBulkJsOpen(false); setBulkJsFile(null); dispatch(clearJobSkillsImportResult()); };
    const runJsBulk   = () => {
        if (!bulkJsFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel." }); return; }
        dispatch(importJobSkillsExcelRequest(bulkJsFile, bulkJsDryRun, bulkJsAction));
    };

    const importErrors = importResult?.errors || importResult?.errorList || [];
    const previewRows  = importResult?.preview || importResult?.rows     || [];
    const jsImportErrors = jsImportResult?.errors || [];
    const jsPreviewRows  = jsImportResult?.preview || [];

    return (
        <>
            {/* ── En-tête ── */}
            <div className="crHeader">
                <div>
                    <h1>Gestion des postes</h1>
                </div>
                <div className="crHeaderActions">
                    <button className="crBtn crBtn--ghost" onClick={() => dispatch(downloadJobsTemplateRequest())}
                            disabled={bulkLoading} type="button">
                        <Download size={16}/> Télécharger la template
                    </button>
                    <button className="crBtn crBtn--primary" onClick={openBulk} type="button">
                        <Upload size={16}/> Ajout massif
                    </button>
                    <button className="crBtn crBtn--primary" onClick={openCreate} type="button">
                        <Plus size={16}/> Ajouter un poste
                    </button>
                    <div style={{width: 1, height: 24, background: "#d1d5db", margin: "0 4px"}}></div>
                    <button className="crBtn crBtn--ghost" onClick={() => dispatch(downloadJobSkillsTemplateRequest())}
                            disabled={jsBulkLoading} type="button">
                        <Download size={16}/> Template de l'affectation
                    </button>
                    <button className="crBtn crBtn--primary" onClick={openJsBulk} type="button">
                        <Layers size={16}/> Affectation massive des compétences
                    </button>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="crToolbar">
                <input type="text" className="crSearch" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Code, nom, description..." />
                <div className="crFilterTabs">
                    {[{ key: "all", label: "Tous" }, { key: "active", label: "Actifs" }, { key: "inactive", label: "Inactifs" }].map((f) => (
                        <button key={f.key} className={`crFilterTab ${filter === f.key ? "active" : ""}`} onClick={() => setFilter(f.key)} type="button">{f.label}</button>
                    ))}
                </div>
                <div className="crMeta">{loading ? "..." : `${filtered.length} poste${filtered.length !== 1 ? "s" : ""}`}</div>
            </div>

            {/* ── Tableau ── */}
            <div className="crWrap">
                <div className="crCard">
                    <div className="crBody">
                        <div className="crTopRow">
                            <h2 className="crTitle">Postes ({loading ? "..." : filtered.length})</h2>
                            <button className="crBtn crBtn--ghost" onClick={() => dispatch(exportJobsRequest())} disabled={loading} type="button" style={{ height: "32px", fontSize: "11px" }}>
                                <FileDown size={14} /> Exporter la liste
                            </button>
                        </div>
                        <div className="crTableWrap">
                            <table className="crTable">
                                <thead>
                                <tr>
                                    <th>Code</th>
                                    <th>Nom du poste</th>
                                    <th>Description</th>
                                    <th>Statut</th>
                                    <th>Date création</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr><td colSpan={6}><div className="crLoading"><div className="crSpinner" /><span>Chargement...</span></div></td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr><td colSpan={6} className="crEmptyCell">Aucun poste trouvé</td></tr>
                                ) : filtered.map((j) => (
                                    <tr key={j.id}>
                                        <td><span className="crStrong">{j.code}</span></td>
                                        <td style={{ fontWeight: 700, color: "#204385", fontSize: 12 }}>{j.name}</td>
                                        <td className="crMuted" style={{ maxWidth: 240, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                            {j.description || "—"}
                                        </td>
                                        <td>
                                            {j.isActive
                                                ? <span className="crBadge crBadge--green">Actif</span>
                                                : <span className="crBadge crBadge--grey">Inactif</span>
                                            }
                                        </td>
                                        <td className="crMuted" style={{ whiteSpace: "nowrap", fontSize: 11 }}>
                                            {j.createdAt ? new Date(j.createdAt).toLocaleDateString("fr-FR") : "—"}
                                        </td>
                                        <td className="is-right">
                                            <div className="crRowActions">
                                                {/* Détails */}
                                                <button className="crIconBtn" title="Voir les détails" onClick={() => onOpenDetails(j)} type="button">
                                                    <Eye size={16} />
                                                </button>
                                                {/* Associer compétences */}
                                                <button className="crIconBtn" title="Association des compétences" onClick={() => onOpenSkills(j)} type="button">
                                                    <BookOpen size={16} />
                                                </button>
                                                {/* Modifier */}
                                                <button className="crIconBtn" title="Modifier" onClick={() => openEdit(j)} type="button">
                                                    <Edit2 size={16} />
                                                </button>
                                                {/* Supprimer */}
                                                <button className="crIconBtn crIconBtn--danger" title="Supprimer" onClick={() => confirmDelete(j)} type="button">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* ══ Modal CRUD poste ══ */}
            <Modal
                open={isModalOpen}
                title={editing ? "Modifier le poste" : "Ajouter un poste"}
                onClose={closeModal}
                size="lg"
                footer={
                    <>
                        <button className="crBtn crBtn--ghost" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                        <button className="crBtn crBtn--primary" onClick={submit} disabled={loading} type="button">
                            <Save size={16} />{loading ? "En cours..." : editing ? "Enregistrer" : "Créer"}
                        </button>
                    </>
                }
            >
                <form className="crFormContent" onSubmit={submit}>
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Identification</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Code *</label>
                                <input name="code" value={form.code} onChange={onChange} placeholder="Ex : DEV-001" maxLength={50} />
                            </div>
                            <div className="crField">
                                <label>Nom du poste *</label>
                                <input name="name" value={form.name} onChange={onChange} placeholder="Ex : Développeur Full Stack" maxLength={200} />
                            </div>
                            <div className="crField crField--full">
                                <label>Description</label>
                                <textarea name="description" value={form.description} onChange={onChange} rows={3} placeholder="Description du poste..." />
                            </div>
                            <div className="crField crField--full">
                                <label className="fmCheck">
                                    <input type="checkbox" name="isActive" checked={form.isActive} onChange={onChange} />
                                    Poste actif
                                </label>
                            </div>
                        </div>
                    </div>
                    <button type="submit" style={{ display: "none" }} />
                </form>
            </Modal>

            {/* ══ Modal Ajout massif ══ */}
            <Modal open={bulkOpen} title="Ajout massif (Excel)" onClose={closeBulk} size="lg"
                   footer={
                       <>
                           <button className="crBtn crBtn--ghost" type="button" onClick={() => dispatch(downloadJobsTemplateRequest())} disabled={bulkLoading}>
                               <FileSpreadsheet size={16} /> Télécharger template
                           </button>
                           <button className="crBtn crBtn--primary" type="button" onClick={runBulk} disabled={bulkLoading}>
                               <Upload size={16} />{bulkLoading ? "Traitement..." : bulkDryRun ? "Prévisualiser" : "Exécuter"}
                           </button>
                       </>
                   }
            >
                <div className="crFormContent">
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Paramètres d'import</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Action</label>
                                <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                    <option value="add">ADD (nouveaux)</option>
                                    <option value="edit">EDIT (mise à jour via code)</option>
                                </select>
                            </div>
                            <div className="crField">
                                <label>Mode</label>
                                <label className="fmCheck">
                                    <input type="checkbox" checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)} />
                                    Prévisualisation avant l'import
                                </label>
                            </div>
                            <div className="crField crField--full">
                                <label>Fichier Excel</label>
                                <input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                            </div>
                        </div>
                    </div>
                    {importResult && (
                        <div className="crFormSection">
                            <h3 className="crFormTitle">Résultat</h3>
                            <div className="fmImportSummary">
                                <span><b>Action :</b> {importResult.action}</span>
                                <span><b>Dry-run :</b> {String(importResult.dryRun)}</span>
                                <span><b>Total :</b> {importResult.totalRows ?? 0}</span>
                                <span><b>OK :</b> {importResult.successCount ?? importResult.createdCount ?? 0}</span>
                                <span><b>Erreurs :</b> {importErrors.length}</span>
                            </div>
                            {importErrors.length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Erreurs</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead><tr><th>Ligne</th><th>Colonne</th><th>Message</th></tr></thead>
                                            <tbody>
                                            {importErrors.map((er, i) => (
                                                <tr key={i}><td className="crMuted">{er.row ?? "—"}</td><td><span className="crStrong">{er.column ?? "—"}</span></td><td className="crMuted">{er.message}</td></tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {previewRows.length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Prévisualisation (max 20)</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead><tr><th>Code</th><th>Nom</th><th>Statut</th></tr></thead>
                                            <tbody>
                                            {previewRows.slice(0, 20).map((p, i) => (
                                                <tr key={i}>
                                                    <td><span className="crStrong">{p.code}</span></td>
                                                    <td className="crMuted">{p.name}</td>
                                                    <td>{p.isActive ? <span className="crBadge crBadge--green">Actif</span> : <span className="crBadge crBadge--grey">Inactif</span>}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="fmHint">Affichage limité à 20 lignes.</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>

            {/* ══ Modal Affectation massive ══ */}
            <Modal open={bulkJsOpen} title="Affectation massive des compétences" onClose={closeJsBulk} size="lg"
                   footer={
                       <>
                           <button className="crBtn crBtn--ghost" onClick={closeJsBulk} disabled={jsBulkLoading} type="button">Annuler</button>
                           <button className="crBtn crBtn--primary" onClick={runJsBulk} disabled={jsBulkLoading} type="button">
                               <Upload size={16} />{jsBulkLoading ? "En cours..." : "Lancer"}
                           </button>
                       </>
                   }
            >
                <div className="crFormContent">
                    <div className="crFormSection">
                        <h3 className="crFormTitle">Configuration de l'import</h3>
                        <div className="crFormGrid">
                            <div className="crField">
                                <label>Mode</label>
                                <select value={bulkJsAction} onChange={(e) => setBulkJsAction(e.target.value)}>
                                    <option value="add">Ajout</option>
                                    <option value="edit">Mise à jour</option>
                                </select>
                            </div>
                            <div className="crField" style={{ display: "flex", alignItems: "flex-end" }}>
                                <label className="fmCheck" style={{ marginBottom: 8 }}>
                                    <input type="checkbox" checked={bulkJsDryRun} onChange={(e) => setBulkJsDryRun(e.target.checked)} />
                                    Prévisualisation avant l'import
                                </label>
                            </div>
                            <div className="crField crField--full">
                                <label>Fichier Excel</label>
                                <input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkJsFile(e.target.files?.[0] || null)} />
                            </div>
                        </div>
                    </div>
                    {jsImportResult && (
                        <div className="crFormSection">
                            <h3 className="crFormTitle">Résultat</h3>
                            <div className="fmImportSummary">
                                <span><b>Action :</b> {jsImportResult.action}</span>
                                <span><b>Dry-run :</b> {String(jsImportResult.dryRun)}</span>
                                <span><b>Total :</b> {jsImportResult.totalRows ?? 0}</span>
                                <span><b>OK :</b> {jsImportResult.successCount ?? jsImportResult.createdCount ?? 0}</span>
                                <span><b>Erreurs :</b> {jsImportErrors.length}</span>
                            </div>
                            {jsImportErrors.length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Erreurs</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead><tr><th>Ligne</th><th>Colonne</th><th>Message</th></tr></thead>
                                            <tbody>
                                            {jsImportErrors.map((er, i) => (
                                                <tr key={i}><td className="crMuted">{er.row ?? "—"}</td><td><span className="crStrong">{er.column ?? "—"}</span></td><td className="crMuted">{er.message}</td></tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            {jsPreviewRows.length > 0 && (
                                <div className="fmBlock">
                                    <div className="fmBlockTitle">Prévisualisation (max 20)</div>
                                    <div className="crTableWrap">
                                        <table className="crTable">
                                            <thead><tr><th>Poste</th><th>Compétence</th><th>Niveau</th><th>Mode</th></tr></thead>
                                            <tbody>
                                            {jsPreviewRows.slice(0, 20).map((p, i) => (
                                                <tr key={i}>
                                                    <td><span className="crStrong">ID: {p.jobId}</span></td>
                                                    <td className="crMuted">{p.skillCode} - {p.skillNom}</td>
                                                    <td><span className="crBadge crBadge--blue">{p.niveau}</span></td>
                                                    <td className="crMuted">{p.modeEvaluationNom || "—"}</td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="fmHint">Affichage limité à 20 lignes.</div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
};

/* ════════════════════════════════════════
   COMPOSANT PRINCIPAL
════════════════════════════════════════ */
const JobManagement = ({ jobs, loading, bulkLoading, importResult, dispatch }) => {
    const [skillsJob,   setSkillsJob]   = useState(null);
    const [skillsOpen,  setSkillsOpen]  = useState(false);
    const [detailsJob,  setDetailsJob]  = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);

    const openSkillsModal   = (job) => { setSkillsJob(job);  setSkillsOpen(true);  };
    const closeSkillsModal  = ()    => { setSkillsOpen(false);  setSkillsJob(null);  };
    const openDetailsModal  = (job) => { setDetailsJob(job); setDetailsOpen(true); };
    const closeDetailsModal = ()    => { setDetailsOpen(false); setDetailsJob(null); };

    return (
        <div className="crPage">
            <JobsTab
                jobs={jobs}
                loading={loading}
                bulkLoading={bulkLoading}
                importResult={importResult}
                dispatch={dispatch}
                onOpenSkills={openSkillsModal}
                onOpenDetails={openDetailsModal}
            />

            <JobSkillsModal
                open={skillsOpen}
                job={skillsJob}
                onClose={closeSkillsModal}
                dispatch={dispatch}
            />

            <JobDetailsModal
                open={detailsOpen}
                job={detailsJob}
                onClose={closeDetailsModal}
                dispatch={dispatch}
            />
            <ScrollToTop/>
        </div>
    );
};

const mapStateToProps = (state) => ({
    jobs:         state.jobs?.jobs         || [],
    loading:      state.jobs?.loading      || false,
    bulkLoading:  state.jobs?.bulkLoading  || false,
    importResult: state.jobs?.importResult || null,
});

const mapDispatchToProps = (dispatch) => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(JobManagement);
